import { action, makeObservable, observable, runInAction } from "mobx";
import LeadAnalyticsAPI from "../api/LeadAnalyticsAPI";
import { FilterValue } from "../models/Filters/FilterValue";
import {
  LeadSection,
  LeadStageAverages,
  LeadStageSummaryRow,
  LeadStatusSummaryItem,
  LeadPipelineStatusSummaryGroup,
} from "../models/Dashboard/LeadAnalytics/LeadAnalytics";
import {
  defaultLeadAnalyticsFilters,
  LeadAnalyticsFilters,
} from "../models/Dashboard/LeadAnalytics/LeadAnalyticsFilters";

const emptyAverages: LeadStageAverages = {
  mql: 0,
  non_contactable: 0,
  open: 0,
  total_time: 0,
};

const emptySectionData = {
  summaryRows: [] as LeadStageSummaryRow[],
  statusSummary: [] as LeadStatusSummaryItem[],
  pipelineStatusSummary: [] as LeadPipelineStatusSummaryGroup[],
  averages: emptyAverages,
  limit: 30,
  totalCount: 0,
  totalPages: 0,
};

export class LeadAnalyticsStore {
  leadAnalyticsAPI: LeadAnalyticsAPI;
  loading = false;
  filters: LeadAnalyticsFilters = defaultLeadAnalyticsFilters();
  selectedFinalStatus: string | null = null;
  selectedStagePipelineId: number | null = null;
  isFiltersExpanded = false;
  summaryRows: LeadStageSummaryRow[] = emptySectionData.summaryRows;
  statusSummary: LeadStatusSummaryItem[] = emptySectionData.statusSummary;
  pipelineStatusSummary: LeadPipelineStatusSummaryGroup[] =
    emptySectionData.pipelineStatusSummary;
  averages: LeadStageAverages = emptySectionData.averages;
  limit = emptySectionData.limit;
  totalCount = emptySectionData.totalCount;
  totalPages = emptySectionData.totalPages;
  owners: FilterValue[] = [];
  pipelines: FilterValue[] = [];
  sources: FilterValue[] = [];

  constructor() {
    this.leadAnalyticsAPI = new LeadAnalyticsAPI();
    makeObservable(this, {
      loading: observable,
      filters: observable,
      selectedFinalStatus: observable,
      selectedStagePipelineId: observable,
      isFiltersExpanded: observable,
      summaryRows: observable,
      statusSummary: observable,
      pipelineStatusSummary: observable,
      averages: observable,
      limit: observable,
      totalCount: observable,
      totalPages: observable,
      owners: observable,
      pipelines: observable,
      sources: observable,
      setFilters: action,
      updateFilters: action,
      setSelectedFinalStatus: action,
      setSelectedStagePipelineId: action,
      setIsFiltersExpanded: action,
      setLoading: action,
      resetFilters: action,
      fetchLeadStageSummary: action,
      fetchOwners: action,
      fetchPipelines: action,
      fetchSources: action,
    });
  }

  setFilters = (filters: LeadAnalyticsFilters) => {
    this.filters = filters;
  };

  updateFilters = (updater: (prev: LeadAnalyticsFilters) => LeadAnalyticsFilters) => {
    this.filters = updater(this.filters);
  };

  setSelectedFinalStatus = (status: string | null) => {
    this.selectedFinalStatus = status;
  };

  setSelectedStagePipelineId = (pipelineId: number | null) => {
    this.selectedStagePipelineId = pipelineId;
  };

  setIsFiltersExpanded = (expanded: boolean) => {
    this.isFiltersExpanded = expanded;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  resetFilters = () => {
    this.filters = defaultLeadAnalyticsFilters();
    this.selectedFinalStatus = null;
    this.selectedStagePipelineId = null;
  };

  fetchLeadStageSummary = async (filters: LeadAnalyticsFilters) => {
    const response = await this.leadAnalyticsAPI.getLeadStageSummary(filters);
    runInAction(() => {
      this.summaryRows = response.list;
      this.statusSummary = response.status_summary;
      this.pipelineStatusSummary = response.pipeline_status_summary ?? [];
      this.averages = response.averages;
      this.limit = response.limit;
      this.totalCount = response.total_count;
      this.totalPages = response.total_pages;
      this.filters = { ...filters, section: response.section || filters.section };
    });
  };

  fetchOwners = async () => {
    const owners = await this.leadAnalyticsAPI.getLeadOwners();
    runInAction(() => {
      this.owners = owners;
    });
  };

  fetchPipelines = async () => {
    const pipelines = await this.leadAnalyticsAPI.getLeadPipelines();
    runInAction(() => {
      this.pipelines = pipelines;
    });
  };

  fetchSources = async () => {
    const sources = await this.leadAnalyticsAPI.getLeadSources();
    runInAction(() => {
      this.sources = sources;
    });
  };
}
